import { createContext, useContext, useState, ReactNode } from "react";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";

export type RoleMode = "personal" | "employer";

interface RoleModeContextType {
  mode: RoleMode;
  setMode: (mode: RoleMode) => void;
  canAccessMode: (mode: RoleMode) => boolean;
}

const RoleModeContext = createContext<RoleModeContextType | undefined>(undefined);

export function RoleModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<RoleMode>(() => {
    const stored = localStorage.getItem("actrus-role-mode");
    // Default to "personal" mode unless explicitly set to employer
    // This prevents users from getting stuck in employer mode
    return (stored as RoleMode) || "personal";
  });

  const setMode = (newMode: RoleMode) => {
    setModeState(newMode);
    localStorage.setItem("actrus-role-mode", newMode);
  };

  const canAccessMode = (requestedMode: RoleMode) => {
    // Personal mode is always accessible
    if (requestedMode === "personal") return true;
    
    // Employer mode requires employer role in database
    // This will be checked by the hook consumer
    return true; // Permission check happens in routes/components
  };

  return (
    <RoleModeContext.Provider value={{ mode, setMode, canAccessMode }}>
      {children}
    </RoleModeContext.Provider>
  );
}

export function useRoleMode() {
  const context = useContext(RoleModeContext);
  if (!context) {
    throw new Error("useRoleMode must be used within RoleModeProvider");
  }
  return context;
}
